import { useState, useEffect, useCallback } from "react";

const API_BASE = process.env.REACT_APP_API_BASE || "https://your-api-gateway.execute-api.us-east-1.amazonaws.com/prod";
const API_TIMEOUT_MS = 45000;
const RATE_LIMIT_MAX_RETRIES = 5;
const RATE_LIMIT_BASE_DELAY_MS = 3000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function parseApiErrorText(text) {
  if (!text) return "Unknown API error";

  try {
    const parsed = JSON.parse(text);
    if (typeof parsed?.error === "string") {
      try {
        const nested = JSON.parse(parsed.error);
        return nested?.message || parsed.error;
      } catch {
        return parsed.error;
      }
    }
    return parsed?.message || parsed?.error || text;
  } catch {
    return text;
  }
}

function computeRetryDelayMs(retriesLeft, retryAfterHeader) {
  const retryAfterSeconds = Number(retryAfterHeader);
  if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
    return retryAfterSeconds * 1000;
  }

  const attempt = RATE_LIMIT_MAX_RETRIES - retriesLeft;
  const backoff = RATE_LIMIT_BASE_DELAY_MS * (2 ** Math.max(0, attempt - 1));
  const jitter = Math.floor(Math.random() * 500);
  return backoff + jitter;
}

async function apiFetch(path, retries = RATE_LIMIT_MAX_RETRIES) {
  const sep = path.includes("?") ? "&" : "?";
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

  try {
    const res = await fetch(`${API_BASE}${path}${sep}_=${Date.now()}`, {
      signal: controller.signal,
    });

    if (!res.ok) {
      const text = await res.text();

      if (res.status === 429 && retries > 0) {
        const waitMs = computeRetryDelayMs(retries, res.headers.get("Retry-After"));
        await sleep(waitMs);
        return apiFetch(path, retries - 1);
      }

      const parsedMessage = parseApiErrorText(text);
      throw new Error(`API error ${res.status}: ${parsedMessage}`);
    }

    return res.json();
  } catch (err) {
    if (err?.name === "AbortError") {
      if (retries > 0) {
        await sleep(1200);
        return apiFetch(path, retries - 1);
      }
      throw new Error(`API request timed out for ${path}`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

function StatusBadge({ status }) {
  const map = {
    match: { label: "Match", cls: "badge-match" },
    mismatch: { label: "Mismatch", cls: "badge-mismatch" },
    prod_only: { label: "Prod only", cls: "badge-prod" },
    draft_only: { label: "Draft only", cls: "badge-draft" },
  };
  const { label, cls } = map[status] || {};
  return <span className={`badge ${cls}`}>{label}</span>;
}

function StatCard({ value, label, color }) {
  return (
    <div className="stat-card">
      <div className="stat-value" style={{ color }}>{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function mappingRowKey(row) {
  const dimensions = (row.dimensionIds || []).join(",");
  const concepts = (row.conceptIds || []).join(",");
  const metrics = (row.metricIds || []).join(",");

  return [
    row.dataAccessSetId || row.dataAccessSetName || "",
    row.dataAccessPropertyId || "",
    row.permissionId || row.permissionName || "",
    row.analyticObjectId || "",
    dimensions,
    concepts,
    metrics,
  ].join("::");
}

function MappingSection({ rows }) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [setFilter, setSetFilter] = useState("all");
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [permissionFilter, setPermissionFilter] = useState("all");
  const [dimensionFilter, setDimensionFilter] = useState("all");
  const [conceptFilter, setConceptFilter] = useState("all");
  const [metricFilter, setMetricFilter] = useState("all");

  const distinct = (values) => [...new Set(values.filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));

  const setOptions = distinct(rows.map(r => r.dataAccessSetName || r.dataAccessSetId));
  const propertyOptions = distinct(rows.map(r => r.dataAccessPropertyId));
  const permissionOptions = distinct(rows.map(r => r.permissionName || r.permissionId));
  const dimensionOptions = distinct(rows.flatMap(r => r.dimensionIds || []));
  const conceptOptions = distinct(rows.flatMap(r => r.conceptIds || []));
  const metricOptions = distinct(rows.flatMap(r => r.metricIds || []));

  const filteredRows = rows.filter((row) => {
    if (statusFilter !== "all" && row.status !== statusFilter) return false;
    if (setFilter !== "all" && (row.dataAccessSetName || row.dataAccessSetId) !== setFilter) return false;
    if (propertyFilter !== "all" && (row.dataAccessPropertyId || "") !== propertyFilter) return false;
    if (permissionFilter !== "all" && (row.permissionName || row.permissionId) !== permissionFilter) return false;
    if (dimensionFilter !== "all" && !(row.dimensionIds || []).includes(dimensionFilter)) return false;
    if (conceptFilter !== "all" && !(row.conceptIds || []).includes(conceptFilter)) return false;
    if (metricFilter !== "all" && !(row.metricIds || []).includes(metricFilter)) return false;
    return true;
  });

  const counts = {
    total: rows.length,
    match: rows.filter(r => r.status === "match").length,
    prodOnly: rows.filter(r => r.status === "prod_only").length,
    draftOnly: rows.filter(r => r.status === "draft_only").length,
  };

  return (
    <div className="artifact-section">
      <div className="section-header">
        <span className="section-icon">🧭</span>
        <h2>Data Access Mapping</h2>
      </div>

      <div className="stats-row">
        <StatCard value={counts.total} label="Total rows" color="var(--text-primary)" />
        <StatCard value={counts.match} label="Matching" color="var(--green)" />
        <StatCard value={counts.prodOnly} label="Prod only" color="var(--blue)" />
        <StatCard value={counts.draftOnly} label="Draft only" color="var(--teal)" />
      </div>

      <div className="mapping-filters-grid">
        <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">Status: all</option>
          <option value="prod_only">Status: prod only</option>
          <option value="draft_only">Status: draft only</option>
          <option value="match">Status: match</option>
        </select>

        <select className="filter-select" value={setFilter} onChange={e => setSetFilter(e.target.value)}>
          <option value="all">Data Access Set: all</option>
          {setOptions.map(v => <option key={v} value={v}>{v}</option>)}
        </select>

        <select className="filter-select" value={propertyFilter} onChange={e => setPropertyFilter(e.target.value)}>
          <option value="all">Property: all</option>
          {propertyOptions.map(v => <option key={v} value={v}>{v}</option>)}
        </select>

        <select className="filter-select" value={permissionFilter} onChange={e => setPermissionFilter(e.target.value)}>
          <option value="all">Permission: all</option>
          {permissionOptions.map(v => <option key={v} value={v}>{v}</option>)}
        </select>

        <select className="filter-select" value={dimensionFilter} onChange={e => setDimensionFilter(e.target.value)}>
          <option value="all">Dimension: all</option>
          {dimensionOptions.map(v => <option key={v} value={v}>{v}</option>)}
        </select>

        <select className="filter-select" value={conceptFilter} onChange={e => setConceptFilter(e.target.value)}>
          <option value="all">Concept: all</option>
          {conceptOptions.map(v => <option key={v} value={v}>{v}</option>)}
        </select>

        <select className="filter-select" value={metricFilter} onChange={e => setMetricFilter(e.target.value)}>
          <option value="all">Metric: all</option>
          {metricOptions.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>

      {!filteredRows.length ? (
        <div className="empty-state">No mapping rows match the current filters.</div>
      ) : (
        <div className="mapping-table-wrap">
          <table className="mapping-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Environment</th>
                <th>Data Access Set</th>
                <th>Property</th>
                <th>Permission</th>
                <th>Analytic Object</th>
                <th>Dimensions</th>
                <th>Concepts</th>
                <th>Metrics</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row, idx) => (
                <tr key={`${mappingRowKey(row)}-${idx}`}>
                  <td><StatusBadge status={row.status} /></td>
                  <td>{row.source === "both" ? "Both" : row.source === "draft" ? "Draft" : "Production"}</td>
                  <td>{row.dataAccessSetName || row.dataAccessSetId || "—"}</td>
                  <td>{row.dataAccessPropertyId || "(unresolved)"}</td>
                  <td>{row.permissionName || row.permissionId || "—"}</td>
                  <td>{row.analyticObjectId || "—"}</td>
                  <td>{(row.dimensionIds || []).join(", ") || "—"}</td>
                  <td>{(row.conceptIds || []).join(", ") || "—"}</td>
                  <td>{(row.metricIds || []).join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function DataAccessMappingPage() {
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState(null);
  const [selectedProject, setSelectedProject] = useState("");
  const [mappingRows, setMappingRows] = useState(null);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingError, setMappingError] = useState("");

  useEffect(() => {
    apiFetch("/projects")
      .then(data => { setProjects(data.projects || []); setProjectsLoading(false); })
      .catch(err => { setProjectsError(err.message); setProjectsLoading(false); });
  }, []);

  const handleLoad = useCallback(async () => {
    if (!selectedProject) return;
    setMappingLoading(true);
    setMappingError("");
    setMappingRows(null);
    try {
      let mapData;
      try {
        mapData = await apiFetch(`/artifacts/das-mapping?projectId=${selectedProject}`);
      } catch {
        mapData = await apiFetch(
          `/artifacts/das-mapping?projectId=${selectedProject}&maxPermissions=120&maxDataAccessSets=120`
        );
      }
      const normalizedRows = (mapData.rows || []).map(row => ({
        ...row,
        status: "match",
        source: row.projectId || selectedProject,
      }));
      setMappingRows(normalizedRows);
    } catch (err) {
      setMappingError(err.message);
    } finally {
      setMappingLoading(false);
    }
  }, [selectedProject]);

  return (
    <div className="app">
      <header className="cug-header">
        <div className="cug-header-top">
          <div className="cug-title">Data Access Mapping</div>
        </div>
        <div className="cug-subtitle">Single Project View</div>
      </header>

      <div className="control-bar">
        <div className="control-inner">
          <div className="control-group">
            <label className="control-label">Project</label>
            {projectsLoading ? <div className="loading-inline">Loading projects…</div>
              : projectsError ? <div className="error-inline">Error: {projectsError}</div>
              : (
                <select className="project-select" value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
                  <option value="">— select a project —</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.displayName || p.id}</option>)}
                </select>
              )}
          </div>
          <button className="compare-btn" disabled={!selectedProject || mappingLoading} onClick={handleLoad}>
            {mappingLoading ? <><span className="spinner" /> Loading…</> : "Load Mapping"}
          </button>
        </div>
      </div>

      <main className="main-content">
        {mappingError && <div className="error-banner"><strong>Error:</strong> {mappingError}</div>}

        {!mappingRows && !mappingLoading && !mappingError && (
          <div className="empty-hero">
            <div className="empty-icon">🧭</div>
            <div className="empty-title">Select a project to load mapping</div>
            <div className="empty-sub">Choose one project above and load its data access mapping.</div>
          </div>
        )}

        {mappingRows && <MappingSection rows={mappingRows} />}
        {mappingLoading && !mappingRows && (
          <div className="loading-state"><div className="loading-spinner" /><div>Loading Data Access Mapping…</div></div>
        )}
      </main>
    </div>
  );
}
